import { ReparoApi } from './index';

/**
 * The forgot-password screens read `ok` and `error` and never throw, so these
 * wrappers convert ReparoApi's thrown ApiError into that shape rather than
 * pushing try/catch into every handler.
 */
export type ApiResult<T> = { ok: boolean; data?: T; error?: string };

const GENERIC_ERROR = 'Something went wrong';

const messageFrom = (err: unknown): string => {
    const apiError = err as { error?: { message?: string } };

    return apiError?.error?.message || GENERIC_ERROR;
};

const run = async <T>(call: () => Promise<T>): Promise<ApiResult<T>> => {
    try {
        return { ok: true, data: await call() };
    } catch (err) {
        return { ok: false, error: messageFrom(err) };
    }
};

/**
 * Resolves the same way whether or not the address has an account — the server
 * deliberately gives nothing away, so the UI advances to the code step either
 * way.
 */
export const startPasswordReset = (
    email: string,
): Promise<ApiResult<{ success: true }>> =>
    run(() =>
        ReparoApi<{ success: true }>({
            method: 'POST',
            url: '/auth/password-reset/start',
            body: JSON.stringify({ email }),
        }),
    );

/**
 * On success this returns the single-use token that authorises the password
 * change. It has to be held and passed to completePasswordReset — without it
 * the server has no evidence the caller ever received the code.
 */
export type VerifyPasswordResetResult = {
    resetToken: string;
    /**
     * Present only when the address owns logins in several organisations; the
     * reset must then name one. Listed only after the code is proven, so the
     * flow still gives nothing away to someone who cannot read the inbox.
     */
    organizations?: Array<{ id: string; name: string }>;
};

export const verifyPasswordResetOtp = (
    email: string,
    otp: string,
): Promise<ApiResult<VerifyPasswordResetResult>> =>
    run(() =>
        ReparoApi<VerifyPasswordResetResult>({
            method: 'POST',
            url: '/auth/password-reset/verify',
            body: JSON.stringify({ email, otp }),
        }),
    );

/** The picker value meaning "every organisation this email has a login in". */
export const ALL_ORGANIZATIONS = '*';

export const completePasswordReset = (
    resetToken: string,
    newPassword: string,
    organizationId?: string,
): Promise<ApiResult<{ success: true }>> =>
    run(() =>
        ReparoApi<{ success: true }>({
            method: 'POST',
            url: '/auth/password-reset/complete',
            body: JSON.stringify(
                organizationId === ALL_ORGANIZATIONS
                    ? { resetToken, newPassword, allOrganizations: true }
                    : { resetToken, newPassword, organizationId },
            ),
        }),
    );
